import React, { useState } from 'react';
import './index.css';
import { ApprovalRequest } from '../../../../../redux/AllocationRequest';
import { ApprovalModal } from '../ApprovalModal';
import { Eye, X, ShoppingBag } from 'lucide-react';
import CartAllocationsView, { CartAllocationEntry } from './CartAllocationsView';

interface CustomItem {
  displayName: string;
  isAddOn: boolean;
  menuItemId: string;
  productId: string;
  quantity: number;
  tinyId: number;
  unitPrice: number;
  skuId?: string;
}

interface ProductWithCustomItems {
  name: string;
  tier: 'Gold' | 'Silver' | 'Bronze';
  quantity: number;
  price?: number;
  itemType?: string;
  customItems?: CustomItem[];
}

interface ApprovalCardProps {
  request: ApprovalRequest & {
    products: ProductWithCustomItems[];
    cartAllocations?: CartAllocationEntry[];
    requestType?: string;
  };
  onApprove: (request: ApprovalRequest, notes?: string) => void;
  onReject: (request: ApprovalRequest, notes?: string) => void;
  isProcessing?: boolean;
}

// ─── Custom Items Modal ───────────────────────────────────────────────────────
const CustomItemsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  customItems: CustomItem[];
}> = ({ isOpen, onClose, productName, customItems }) => {
  if (!isOpen) return null;

  const total = customItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', margin: '16px' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafafa' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: '#fff3e0', borderRadius: '8px', padding: '6px', display: 'flex' }}>
              <ShoppingBag size={18} color="#e67e22" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a1a', lineHeight: 1.2 }}>Custom Menu Items</div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{productName}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', display: 'flex', color: '#666' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#eee')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', maxHeight: '400px', overflowY: 'auto' }}>
          {customItems.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999', margin: '20px 0' }}>No custom items found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {customItems.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f9f9f9', borderRadius: '10px', border: '1px solid #efefef' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a' }}>{item.displayName}</span>
                      {item.isAddOn && (
                        <span style={{ fontSize: '10px', fontWeight: 600, background: '#e8f5e9', color: '#2e7d32', padding: '2px 7px', borderRadius: '20px' }}>Add-on</span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '3px' }}>SKU ID: {item.skuId || 'No SKU ID'}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>×{item.quantity}</div>
                    <div style={{ fontSize: '12px', color: '#e67e22', fontWeight: 600 }}>Rs. {item.unitPrice.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {customItems.length > 0 && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa' }}>
            <span style={{ fontSize: '13px', color: '#666' }}>
              {customItems.length} item(s) · {customItems.reduce((s, i) => s + i.quantity, 0)} units
            </span>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>Total: Rs. {total.toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main ApprovalCard ────────────────────────────────────────────────────────
export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  request,
  onApprove,
  onReject,
  isProcessing = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [customItemsModal, setCustomItemsModal] = useState<{
    open: boolean;
    productName: string;
    items: CustomItem[];
  }>({ open: false, productName: '', items: [] });

  const isCartAllocation =
    request.requestType === 'Cart_Allocation' ||
    request.requestType === 'Bulk_Cart_Allocation' ||
    request.type === 'Cart Allocation' ||
    (request as any).type === 'Bulk_Cart_Allocation';

  const cartAllocations: CartAllocationEntry[] = (request as any).cartAllocations || [];

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved': return 'badge-approved';
      case 'rejected': return 'badge-rejected';
      case 'pending':  return 'badge-pending';
      default:         return 'badge-default';
    }
  };

  const getStatusText = (status: string) =>
    status.charAt(0).toUpperCase() + status.slice(1);

  const handleActionClick = (action: 'approve' | 'reject') => {
    setActionType(action);
    setShowModal(true);
  };

  const handleConfirm = (notes: string) => {
    if (actionType === 'approve') onApprove(request, notes);
    else if (actionType === 'reject') onReject(request, notes);
    setShowModal(false);
    setActionType(null);
  };

  const handleViewCustomItems = (product: ProductWithCustomItems) => {
    setCustomItemsModal({ open: true, productName: product.name, items: product.customItems || [] });
  };

  const handleViewCustomItemsByName = (productName: string, items: CustomItem[]) => {
    setCustomItemsModal({ open: true, productName, items });
  };

  const isProcessed = request.status === 'approved' || request.status === 'rejected';
  const isDisabled = isProcessed || isProcessing;

  const getTypeLabel = () => {
    const t = request.requestType || request.type || '';
    if (t === 'Bulk_Cart_Allocation') return 'Bulk Cart Allocation';
    if (t === 'Cart_Allocation') return 'Cart Allocation';
    return t;
  };

  return (
    <>
      <div className="approval-card">
        {/* Header */}
        <div className="card-header-section">
          <div className="header-left">
            <h3 className="request-id">{request.id}</h3>
            <p className="request-type">{getTypeLabel()}</p>
          </div>
          <div className="header-right">
            <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
              {getStatusText(request.status)}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="card-details">
          <div className="detail-row">
            <span className="detail-label">Department:</span>
            <span className="detail-value">{request.department}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Requester:</span>
            <span className="detail-value">{request.requester}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Summary:</span>
            <span className="detail-value">{request.summary}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Target Date:</span>
            <span className="detail-value">{request.targetDate}</span>
          </div>
          {request.notes && (
            <div className="detail-row notes">
              <span className="detail-label">Notes:</span>
              <span className="detail-value">{request.notes}</span>
            </div>
          )}
        </div>

        {/* Products section — cart allocation or standard list */}
        {isCartAllocation && cartAllocations.length > 0 ? (
          <CartAllocationsView
            cartAllocations={cartAllocations}
            onViewCustomItems={handleViewCustomItemsByName}
          />
        ) : (
          <div className="products-preview">
            <div className="products-header">
              <span className="products-title">Products</span>
              <span className="products-count">
                {request.totalItems} item(s), {request.totalUnits} units
              </span>
            </div>
            <div className="products-list">
              {(request.products as ProductWithCustomItems[]).map((product, idx) => {
                const isCustomMenu =
                  product.itemType === 'CustomMenu' &&
                  product.customItems &&
                  product.customItems.length > 0;

                return (
                  <div key={idx} className="product-badge" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className={`tier-badge ${product.tier.toLowerCase()}`}>{product.tier}</span>
                    <span className="product-name">{product.name}</span>
                    <span className="product-qty">{product.quantity} packs</span>
                    {isCustomMenu && (
                      <button
                        onClick={() => handleViewCustomItems(product)}
                        title="View custom menu items"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: '5px', display: 'inline-flex', alignItems: 'center', color: '#e67e22', transition: 'background 0.15s', flexShrink: 0 }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#fff3e0')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                      >
                        <Eye size={14} strokeWidth={2.2} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="card-actions">
          {!isProcessed ? (
            <>
              <button className="btn-reject" onClick={() => handleActionClick('reject')} disabled={isDisabled}>
                {isProcessing ? 'Processing...' : 'Reject'}
              </button>
              <button className="btn-approve" onClick={() => handleActionClick('approve')} disabled={isDisabled}>
                {isProcessing ? 'Processing...' : 'Approve'}
              </button>
            </>
          ) : (
            <div className="processed-message text-muted text-center py-2">
              {request.status === 'approved' ? '✓ Request approved' : '✗ Request rejected'}
            </div>
          )}
        </div>
      </div>

      <ApprovalModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        actionType={actionType}
        request={request}
      />

      <CustomItemsModal
        isOpen={customItemsModal.open}
        onClose={() => setCustomItemsModal({ open: false, productName: '', items: [] })}
        productName={customItemsModal.productName}
        customItems={customItemsModal.items}
      />
    </>
  );
};